import classNames from 'classnames';
import {
  type ButtonHTMLAttributes,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { mergeRefs } from '../../utils/merge-refs';
import styles from './Button.module.css';
import { useButtonGroup } from './ButtonGroupContext';
import spinnerStyles from './ButtonSpinner.module.css';

const warned = new Set<string>();
function warnOnce(message: string) {
  if (process.env.NODE_ENV !== 'production' && !warned.has(message)) {
    warned.add(message);
    console.warn(message);
  }
}

type ButtonColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'lightBlue'
  | 'destructive';
/** `bare` leaves fill, border, padding and size to the consumer's class. */
export type ButtonVariant = 'default' | 'outlined' | 'ghost' | 'link' | 'bare';
type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';
type ButtonShape = 'squared' | 'rounded';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: ButtonColor;
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  /** Optional label to show next to the spinner while loading. */
  loadingText?: ReactNode;
  /** Renders the pressed/selected visual state. */
  isActive?: boolean;
  /** Merge styling onto the single child element instead of a <button>. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      children,
      color,
      variant,
      size,
      shape = 'squared',
      leftIcon,
      rightIcon,
      fullWidth = false,
      loading = false,
      loadingText,
      isActive = false,
      asChild = false,
      className,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      type = 'button',
      disabled,
      ...rest
    },
    ref
  ) {
    const group = useButtonGroup();
    const resolvedColor = color ?? group?.color ?? 'default';
    const resolvedVariant = variant ?? group?.variant ?? 'default';
    const resolvedSize = size ?? group?.size ?? 'md';
    const isDisabled = disabled ?? group?.disabled ?? false;

    const isBare = resolvedVariant === 'bare';

    const rootClassName = isBare
      ? classNames(styles.bare, fullWidth && styles.fullWidth, className)
      : classNames(
          styles.button,
          styles[resolvedColor],
          styles[resolvedVariant],
          styles[resolvedSize],
          styles[shape],
          fullWidth && styles.fullWidth,
          className
        );

    if (asChild && isValidElement(children)) {
      if (isDisabled || loading) {
        warnOnce(
          'Button with asChild only merges styling onto the child; `disabled` and `loading` are ignored. Handle them on the child element.'
        );
      }
      const child = children as ReactElement;
      const childProps = child.props as { className?: string };
      const childRef = (
        child as ReactElement & { ref?: Ref<HTMLButtonElement> }
      ).ref;
      return cloneElement(child, {
        ...rest,
        // Conditional spreads: cloneElement config keys override child props
        // even when undefined, which would wipe a child's own aria-label.
        ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
        ...(ariaLabelledBy !== undefined
          ? { 'aria-labelledby': ariaLabelledBy }
          : {}),
        ref: mergeRefs([ref, childRef]),
        className: classNames(rootClassName, childProps.className),
        ...(isActive ? { 'data-state': 'active' } : {}),
      });
    }

    const isIconOnly = (leftIcon || rightIcon) && !children;

    if (isIconOnly && !ariaLabel && !ariaLabelledBy) {
      warnOnce(
        'Icon-only Button requires aria-label or aria-labelledby for accessibility.'
      );
    }

    const content =
      loading && loadingText !== undefined ? loadingText : children;

    return (
      <button
        ref={ref}
        className={rootClassName}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-busy={loading || undefined}
        data-state={isActive ? 'active' : undefined}
        type={type}
        disabled={isDisabled || loading}
        {...rest}
      >
        {loading ? (
          <span className={spinnerStyles.spinner} aria-hidden="true" />
        ) : (
          leftIcon && <span aria-hidden="true">{leftIcon}</span>
        )}
        {content && (isBare ? content : <span>{content}</span>)}
        {rightIcon && <span aria-hidden="true">{rightIcon}</span>}
      </button>
    );
  }
);
